import React, { useEffect, useState } from "react";
import { db, auth } from "../firebase";
import {
    collection,
    getDocs,
    deleteDoc,
    doc,
} from "firebase/firestore";
import {
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
} from "firebase/auth";

const Admin = () => {
    const [user, setUser] = useState(null);
    const [users, setUsers] = useState([]);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const [filterKey, setFilterKey] = useState("all");
    const [search, setSearch] = useState("");

    const fields = [
        { label: "Toate datele", value: "all" },
        { label: "Nume", value: "fullName" },
        { label: "Vârstă", value: "age" },
        { label: "Comunitate", value: "community" },
        { label: "Gen", value: "gender" },
        { label: "Clasă", value: "className" },
    ];

    useEffect(() => {
        const unsub = onAuthStateChanged(auth, (u) => {
            setUser(u);
            if (u) fetchData();
        });
        return () => unsub();
    }, []);

    const login = async () => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
        } catch (err) {
            console.error(err);
            alert("Login failed");
        }
    };

    const fetchData = async () => {
        try {
            const snapshot = await getDocs(collection(db, "pasapoarteCivice"));

            const data = snapshot.docs.map((d) => ({
                id: d.id,
                ...d.data(),
            }));

            setUsers(data);
        } catch (err) {
            console.error("Fetch error:", err);
        }
    };

    const deleteUser = async (id) => {
        await deleteDoc(doc(db, "pasapoarteCivice", id));
        fetchData();
    };

    const deleteAll = async () => {
        if (!window.confirm("Sigur vrei să ștergi TOT?")) return;

        const snapshot = await getDocs(collection(db, "pasapoarteCivice"));

        await Promise.all(
            snapshot.docs.map((d) =>
                deleteDoc(doc(db, "pasapoarteCivice", d.id))
            )
        );

        fetchData();
    };

    const logout = async () => {
        await signOut(auth);
        setUsers([]);
    };

    const truncate = (text, max = 100) => {
        if (!text) return "";
        const str = String(text);
        return str.length > max ? str.slice(0, max) + "..." : str;
    };

    const formatValue = (value) => {
        if (value && value.seconds) {
            return new Date(value.seconds * 1000).toLocaleString();
        }

        if (typeof value === "object" && value !== null) {
            return JSON.stringify(value);
        }

        return value ?? "";
    };

    const filteredUsers = users.filter((item) => {
        if (!search.trim()) return true;

        const searchText = search.toLowerCase();

        if (filterKey === "all") {
            return Object.values(item).some((value) =>
                String(formatValue(value)).toLowerCase().includes(searchText)
            );
        }

        return String(formatValue(item[filterKey]))
            .toLowerCase()
            .includes(searchText);
    });

    const sortedUsers = [...filteredUsers].sort((a, b) => {
        if (filterKey === "all") return 0;

        return String(formatValue(a[filterKey]))
            .localeCompare(String(formatValue(b[filterKey])));
    });

    if (!user) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#0B0F2A] text-white">
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 p-8 rounded-2xl shadow-2xl w-[320px]">
                    <h2 className="text-center text-lg tracking-widest mb-6 opacity-70">
                        ADMIN LOGIN
                    </h2>

                    <input
                        placeholder="Email"
                        className="w-full mb-4 p-2 bg-white/5 border border-white/10 rounded-md outline-none"
                        onChange={(e) => setEmail(e.target.value)}
                    />

                    <input
                        type="password"
                        placeholder="Password"
                        className="w-full mb-6 p-2 bg-white/5 border border-white/10 rounded-md outline-none"
                        onChange={(e) => setPassword(e.target.value)}
                    />

                    <button
                        onClick={login}
                        className="w-full py-2 bg-gradient-to-r from-indigo-500 to-blue-500 rounded-md hover:opacity-90 transition"
                    >
                        Sign In
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#0B0F2A] text-white p-8">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl tracking-widest opacity-80">
                    ADMIN DASHBOARD
                </h1>

                <div className="flex gap-3">
                    <button
                        onClick={fetchData}
                        className="px-4 py-2 text-sm bg-white/10 rounded-md hover:bg-white/20 transition"
                    >
                        Reload
                    </button>

                    <button
                        onClick={deleteAll}
                        className="px-4 py-2 text-sm bg-red-500/80 rounded-md hover:bg-red-500 transition"
                    >
                        Delete All
                    </button>

                    <button
                        onClick={logout}
                        className="px-4 py-2 text-sm bg-gray-500/70 rounded-md hover:bg-gray-500 transition"
                    >
                        Logout
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-4 mb-8">
                <select
                    value={filterKey}
                    onChange={(e) => setFilterKey(e.target.value)}
                    className="bg-white/10 border border-white/10 rounded-md px-4 py-2 outline-none text-sm"
                >
                    {fields.map((field) => (
                        <option
                            key={field.value}
                            value={field.value}
                            className="bg-[#0B0F2A]"
                        >
                            {field.label}
                        </option>
                    ))}
                </select>

                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Caută..."
                    className="bg-white/10 border border-white/10 rounded-md px-4 py-2 outline-none text-sm w-[260px]"
                />
            </div>

            {sortedUsers.length === 0 && (
                <p className="text-center text-white/40">
                    No data available
                </p>
            )}

            {filterKey !== "all" && (
                <div className="backdrop-blur-xl bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-white/10">
                        <tr>
                            <th className="text-left p-4 text-white/50">
                                #
                            </th>
                            <th className="text-left p-4 text-white/50">
                                {fields.find((f) => f.value === filterKey)?.label}
                            </th>
                            <th className="text-right p-4 text-white/50">
                                Acțiune
                            </th>
                        </tr>
                        </thead>

                        <tbody>
                        {sortedUsers.map((item, index) => (
                            <tr
                                key={item.id}
                                className="border-t border-white/10"
                            >
                                <td className="p-4 text-white/40">
                                    {index + 1}
                                </td>

                                <td className="p-4">
                                    {formatValue(item[filterKey]) || "-"}
                                </td>

                                <td className="p-4 text-right">
                                    <button
                                        onClick={() => deleteUser(item.id)}
                                        className="text-xs px-4 py-1 rounded-md bg-white/10 hover:bg-red-500/60 transition"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}

            {filterKey === "all" && (
                <div className="grid md:grid-cols-2 gap-6">
                    {sortedUsers.map((item) => (
                        <div
                            key={item.id}
                            className="backdrop-blur-xl bg-white/5 border border-white/10 p-6 rounded-2xl shadow-lg hover:shadow-2xl transition"
                        >
                            <div className="space-y-2 text-sm">
                                {Object.entries(item).map(([key, value]) => {
                                    const displayValue = formatValue(value);

                                    return (
                                        <p
                                            key={key}
                                            className="flex justify-between gap-4"
                                        >
                                            <span className="text-white/40">
                                                {key}
                                            </span>

                                            <span
                                                className="text-right max-w-[60%] break-words break-all overflow-hidden"
                                                title={String(displayValue)}
                                            >
                                                {truncate(displayValue, 100)}
                                            </span>
                                        </p>
                                    );
                                })}
                            </div>

                            <div className="mt-6 flex justify-end">
                                <button
                                    onClick={() => deleteUser(item.id)}
                                    className="text-xs px-4 py-1 rounded-md bg-white/10 hover:bg-red-500/60 transition"
                                >
                                    Delete
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Admin;